import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog' 
import Button from '@/components/Button'
import { Input } from '@/components/ui/input'  
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form' 
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'  
import { useEffect, useRef } from 'react'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'  
import { useToast } from '@/components/ui/use-toast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateResearchedProduct } from '@/api/business/marketResearch'
import { ResearchedProduct } from '@/types/MarketResearch'
import ImageProductMarketResearch from './ImageProductMarketResearch'
import { ResearchedProductKeys } from '@/queries/useResearchedProductQuery'

interface AddDialogProps {
    isOpen: boolean
    onClose: () => void 
    itemEdit: ResearchedProduct
    handleViewImage: (key: string) => void
}

const EditProductMarketResearchDialog = ({ itemEdit, isOpen, onClose, handleViewImage }: AddDialogProps) => { 
    const currentLocation = useRef({} as GeolocationPosition);  
    const queryClient = useQueryClient()
     
    const online = useOnlineStatus();
    const { toast } = useToast()  
  

    const schema = z.object({ 
        DS_NOME_FANTASIA: z.string(),
        DS_PRODUTO: z.string(),
        SK_PRODUTO: z.string().min(1, {
            message: 'O produto é obrigatório',
        }),
        VL_PRECO_COMPRA: z.number(),
        QTD_VOLUME_COMPRA: z.number(),
        VL_PRECO_VENDA: z.number()
    });

     const form = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {   
            SK_PRODUTO: itemEdit.SK_PRODUTO || "",
            VL_PRECO_COMPRA: itemEdit.VL_PRECO_COMPRA || 0,
            QTD_VOLUME_COMPRA: itemEdit.QTD_VOLUME_COMPRA || 0,
            VL_PRECO_VENDA: itemEdit.VL_PRECO_VENDA || 0,
            DS_NOME_FANTASIA: itemEdit.DS_NOME_FANTASIA || "",
            DS_PRODUTO: itemEdit.DS_PRODUTO || "",
        },
    })

    useEffect(() => {
        form.reset({ 
            SK_PRODUTO: itemEdit.SK_PRODUTO || "",
            VL_PRECO_COMPRA: itemEdit.VL_PRECO_COMPRA || 0,
            QTD_VOLUME_COMPRA: itemEdit.QTD_VOLUME_COMPRA || 0,
            VL_PRECO_VENDA: itemEdit.VL_PRECO_VENDA || 0,
            DS_NOME_FANTASIA:  itemEdit.DS_NOME_FANTASIA || "",
            DS_PRODUTO: itemEdit.DS_PRODUTO || "",
        })
        console.log(itemEdit)
    }, [itemEdit])
 
    const formatCurrencyBR = (value: number | string) => {
        if (!value) return ""

        const number =
            typeof value === "string"
            ? Number(value.replace(/\D/g, "")) / 100
            : value

        return number.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
        })
    }

    const parseCurrencyBR =  (value: string) => {
        return Number(value.replace(/\D/g, "")) / 100
    }

    const showLocationFailedAlert = (data: z.infer<typeof schema>) => {
        toast({
                title: 'Permissão negada pelo usuário.',
                description: "A aplicação precisa da permissão de localização para funcionamento, por favor ative a localização.",
                variant: 'destructive',
            })
        
        onSave(data)
    }

    const onSubmit = (data: z.infer<typeof schema>) => {  
        navigator.geolocation.getCurrentPosition((position) => {
            currentLocation.current = position; 
            onSave(data)
        }, () => showLocationFailedAlert(data)); 
        form.reset();
    }

    const { mutate, isPending } = useMutation({
        mutationFn: updateResearchedProduct,
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ResearchedProductKeys.details() })
            toast({
                title: 'Produto adicionado na pesquisa'
            })
            onClose()
        },
        onError: () => {
            let errorMessage =
                'Não foi possível adicionar o produto. Tente novamente mais tarde.'

            toast({
                title: 'Erro ao adicionar o produto',
                description: errorMessage,
                variant: 'destructive',
            })
        },
    })

    const onSave = async (data: z.infer<typeof schema>) => {
         
        mutate({
            SK_PRODUTOS_PESQUISADOS: itemEdit.SK_PRODUTOS_PESQUISADOS,
            VL_PRECO_COMPRA: data.VL_PRECO_COMPRA,
            QTD_VOLUME_COMPRA: data.QTD_VOLUME_COMPRA,
            VL_PRECO_VENDA: data.VL_PRECO_VENDA
        }) 
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-w-[600px] overflow-auto' aria-describedby={undefined}>
                <DialogHeader className='px-2'>
                    <DialogTitle>Informar produto na pesquisa</DialogTitle>
                </DialogHeader>
                <Form {...form}>
                    <form
                        onSubmit={form.handleSubmit(onSubmit)}
                        className="space-y-2 px-2 overflow-hidden overflow-y-auto"
                    >
                        <div className='grid grid-cols-[1fr] gap-2'> 
                             <div
                                className={`fixed top-3 right-3 z-50 px-3 py-1 rounded-full text-xs font-medium
                                    ${online ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}
                                `}
                                >
                                {online ? "Online" : "Offline"}
                            </div> 
                            <FormItem>
                                <FormLabel>Cliente</FormLabel> 
                                <Input 
                                    disabled 
                                    value={itemEdit.DS_NOME_FANTASIA}
                                />
                                <FormMessage />
                            </FormItem> 

                             <FormItem>
                                <FormLabel>Produto</FormLabel> 
                                <Input 
                                    disabled 
                                    value={itemEdit.DS_PRODUTO}
                                />
                                <FormMessage />
                            </FormItem>  

                            <FormField
                                name="VL_PRECO_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de compra</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                name="QTD_VOLUME_COMPRA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Volume em Caixa Semanal</FormLabel>
                                        <FormControl>
                                            <Input {...field}  onChange={(e) => field.onChange(parseInt(e.target.value))} type='number'/>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            /> 

                            <FormField
                                name="VL_PRECO_VENDA"
                                control={form.control}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Preço de venda</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                inputMode="numeric"
                                                placeholder='R$ 0,00'
                                                value={formatCurrencyBR(field.value)}
                                                onChange={(e) =>
                                                    field.onChange(parseCurrencyBR(e.target.value))
                                                }
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                        <ImageProductMarketResearch handleViewImage={handleViewImage} itemEdit={itemEdit}/>
 

                        </div>    
                        <DialogFooter>
                             <div className='gap-6 grid grid-flow-col'> 
                                <Button
                                    type="button"
                                    onClick={onClose}
                                    variant="outline"
                                >
                                    Cancelar
                                </Button>
                                <Button isLoading={isPending} type="submit">
                                    Confirmar
                                </Button>
                            </div>
                        </DialogFooter> 
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}

export default EditProductMarketResearchDialog
